import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Chip,
  Divider,
  ButtonGroup,
} from "@mui/material";
import { useSelector } from "react-redux";
import orderService from "../../../../services/orderService";
import {
  LineChart,
  BarChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import InboxIcon from "@mui/icons-material/Inbox";
import UpdateIcon from "@mui/icons-material/Update";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import BarChartIcon from "@mui/icons-material/BarChart";
import TableRowsIcon from "@mui/icons-material/TableRows";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import InsertChartIcon from "@mui/icons-material/InsertChart";
import { format, parse, addDays, setISOWeek, startOfYear } from "date-fns";
import { tr } from "date-fns/locale";
import { OrderStats } from "./OrderStats";

const ReportSection = ({ title, data, columns, loading, chart }) => {
  const [activeTab, setActiveTab] = useState("chart");

  // İlk değeri ve son değeri alarak büyüme oranını hesapla
  const calculateGrowth = () => {
    if (!data || data.length < 2) return null;

    const firstValue = data[0]?.TotalSales || 0;
    const lastValue = data[data.length - 1]?.TotalSales || 0;

    if (firstValue === 0) return null;

    const growthRate = ((lastValue - firstValue) / firstValue) * 100;
    return growthRate.toFixed(1);
  };

  const growthRate = calculateGrowth();
  const isPositiveGrowth = growthRate > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: { xs: 2, sm: 3 },
        overflow: "hidden",
        border: "1px solid rgba(0,0,0,0.06)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
        display: "flex",
        flexDirection: "column",
        bgcolor: "white",
        position: "relative",
      }}
    >
      {/* Üst panel - başlık ve özet metrikler */}
      <Box
        sx={{
          p: { xs: 2, sm: 2.5, md: 3 },
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1rem", sm: "1.1rem" },
              color: "text.primary",
              mb: 0.5,
            }}
          >
            {title}
          </Typography>

          {/* Büyüme oranı göstergesi */}
          {growthRate && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {isPositiveGrowth ? (
                <TrendingUpIcon
                  sx={{
                    color: "success.main",
                    fontSize: 18,
                    mr: 0.5,
                  }}
                />
              ) : (
                <TrendingDownIcon
                  sx={{
                    color: "error.main",
                    fontSize: 18,
                    mr: 0.5,
                  }}
                />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: isPositiveGrowth ? "success.main" : "error.main",
                }}
              >
                {isPositiveGrowth ? "+" : ""}
                {growthRate}%
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  ml: 0.5,
                }}
              >
                dönem bazında
              </Typography>
            </Box>
          )}
        </Box>

        {/* Sağ taraftaki kontroller */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <ButtonGroup
            variant="outlined"
            size="small"
            sx={{
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              ".MuiButtonGroup-grouped": {
                border: "1px solid rgba(0,0,0,0.08)",
              },
            }}
          >
            <Button
              onClick={() => setActiveTab("chart")}
              sx={{
                px: 2,
                bgcolor:
                  activeTab === "chart" ? "primary.light" : "transparent",
                color: activeTab === "chart" ? "white" : "text.secondary",
                "&:hover": {
                  bgcolor:
                    activeTab === "chart" ? "primary.main" : "rgba(0,0,0,0.04)",
                },
              }}
            >
              <BarChartIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Grafik
            </Button>
            <Button
              onClick={() => setActiveTab("table")}
              sx={{
                px: 2,
                bgcolor:
                  activeTab === "table" ? "primary.light" : "transparent",
                color: activeTab === "table" ? "white" : "text.secondary",
                "&:hover": {
                  bgcolor:
                    activeTab === "table" ? "primary.main" : "rgba(0,0,0,0.04)",
                },
              }}
            >
              <TableRowsIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Tablo
            </Button>
          </ButtonGroup>
        </Box>
      </Box>

      {/* Ana içerik alanı */}
      <Box
        sx={{
          p: { xs: 2, sm: 2.5, md: 3 },
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {/* Yükleme göstergesi */}
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.8)",
              zIndex: 10,
              backdropFilter: "blur(1px)",
            }}
          >
            <CircularProgress size={36} />
          </Box>
        )}

        {/* Veri görselleştirme alanı */}
        <Box
          sx={{
            display: activeTab === "chart" ? "block" : "none",
            flexGrow: 1,
          }}
        >
          {chart && data.length > 0 ? (
            chart(data)
          ) : (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                p: 3,
              }}
            >
              <InsertChartIcon
                sx={{ fontSize: 48, color: "text.disabled", mb: 2 }}
              />
              <Typography
                variant="body1"
                sx={{ color: "text.secondary", mb: 1 }}
              >
                Görselleştirilecek veri bulunamadı
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.disabled", textAlign: "center" }}
              >
                Seçili tarih aralığında veri bulunmuyor.
                <br />
                Lütfen filtre kriterlerinizi değiştirin.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Tablo görünümü */}
        <Box
          sx={{
            display: activeTab === "table" ? "block" : "none",
            flexGrow: 1,
          }}
        >
          {columns && data.length > 0 ? (
            <TableContainer sx={{ height: "100%", overflowY: "auto" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((col, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          fontWeight: 700,
                          bgcolor: "grey.50",
                          borderBottom: "2px solid rgba(0,0,0,0.1)",
                        }}
                      >
                        {col.header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, rowIndex) => (
                    <TableRow
                      key={rowIndex}
                      sx={{
                        "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                        "&:last-child td": { borderBottom: 0 },
                      }}
                    >
                      {columns.map((col, colIndex) => (
                        <TableCell key={colIndex}>
                          {col.accessor(row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography color="text.secondary">Veri bulunamadı</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

const StatisticCard = ({ title, value, icon, color }) => (
  <Paper
    elevation={2}
    sx={{
      p: { xs: 2, sm: 2.5, md: 3 },
      height: "100%",
      background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
      color: "white",
      borderRadius: 2,
      transition: "all 0.3s ease",
      "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: 6,
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Box>
        <Typography
          variant="subtitle2"
          sx={{ opacity: 0.8, fontSize: { xs: "0.875rem", sm: "1rem" } }}
        >
          {title}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            mt: 1,
            fontWeight: "bold",
            fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
          }}
        >
          {value}
        </Typography>
      </Box>
      {icon}
    </Box>
  </Paper>
);

const TopProductCard = ({ date, productName, quantity }) => (
  <Paper
    elevation={1}
    sx={{
      p: { xs: 1.5, sm: 2 },
      borderRadius: { xs: 1.5, sm: 2 },
      background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
      border: "1px solid #e0e0e0",
      transition: "all 0.2s ease",
      "&:hover": {
        boxShadow: (theme) => theme.shadows[2],
        transform: "translateY(-1px)",
        borderColor: "primary.main",
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            mb: 0.5,
            color: "text.primary",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: 1.3,
            fontSize: { xs: "0.875rem", sm: "1rem" },
          }}
        >
          {productName}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <CalendarTodayIcon sx={{ fontSize: 14, color: "text.secondary" }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
          >
            {date}
          </Typography>
        </Box>
      </Box>
      <Chip
        label={`${quantity} Adet`}
        color="primary"
        size="small"
        sx={{
          borderRadius: 1,
          fontWeight: 500,
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          "& .MuiChip-label": {
            px: 1.5,
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
          },
        }}
      />
    </Box>
  </Paper>
);
const formatCurrency = (value) =>
  value?.toLocaleString("tr-TR", { style: "currency", currency: "TRY" }) || "-";

export const OrderReports = () => {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState({});
  const authUserId = useSelector((state) => state?.auth?.user?.id);

  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return {
      startDate: firstDay.toISOString().split("T")[0],
      endDate: lastDay.toISOString().split("T")[0],
    };
  });

  const formatCurrency = (value) => {
    const formattedValue =
      value?.toLocaleString("tr-TR", { style: "currency", currency: "TRY" }) ||
      "-";
    return formattedValue.replace("₺", "") + " ₺";
  };

  const getReports = async () => {
    try {
      setLoading(true);
      const reportsData = {};

      for (let type = 1; type <= 14; type++) {
        const request = {
          userId: authUserId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          type,
        };

        const response = await orderService.fetchOrdersReports(request);
        if (response.status && response.data) {
          switch (type) {
            case 1: //haftalık satış oranı
              reportsData.weeklySales = response.data;
              break;
            case 2: //aylık satış oranı
              reportsData.monthlySales = response.data;
              break;
            case 3: //aylık ödenecek kdv tutarı
              reportsData.monthlyVat = response.data;
              break;
            case 4: //haftalık ödenecek kdv tutarı
              reportsData.weeklyVat = response.data;
              break;
            case 5: //aylık en çok satan ürün
              reportsData.monthlyTopProducts = response.data;
              break;
            case 6: //haftalık en çok satan ürün
              reportsData.weeklyTopProducts = response.data;
              break;

            case 7: //Aylık toplam indirim
              reportsData.monthlyDiscounts = response.data;
              break;
            case 8: //haftalık toplam indirim
              reportsData.weeklyDiscounts = response.data;
              break;

            case 9: //aylık iptal tutarı
              reportsData.monthlyCancellations = response.data;
              break;
            case 10: //Haftalık iptal tutarı
              reportsData.weeklyCancellations = response.data;
              break;

            case 11: //Aylık iade edilen tutarı
              reportsData.monthlyReturns = response.data;
              break;
            case 12: //Haftalık iade edilen tutarı
              reportsData.weeklyReturns = response.data;
              break;
            // Diğer rapor tipleri buraya eklenebilir
          }
        }
      }

      // Seçili tarih aralığındaki toplam sipariş sayısı ve toplam ciroyu hesapla
      // Sadece monthlySales kullanıyoruz çünkü weeklySales aynı verileri farklı gruplamayla gösteriyor
      let totalOrderCount = 0;
      let totalRevenue = 0;
      
      // Sadece monthlySales array'indeki tüm değerleri topla
      if (reportsData.monthlySales && Array.isArray(reportsData.monthlySales)) {
        reportsData.monthlySales.forEach(item => {
          totalOrderCount += item.TotalSaleCount || 0;
          totalRevenue += item.TotalSales || 0;
        });
      }
      
      // Hesaplanan değerleri reportsData'ya ekle ve state'e kaydet
      setReports({
        ...reportsData,
        dateRangeTotalOrderCount: totalOrderCount,
        dateRangeTotalRevenue: totalRevenue
      });
    } catch (error) {
      console.error("Raporlar alınırken hata oluştu:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getReports();
  }, [dateRange]);

  const handleDateChange = (e) => {
    setDateRange((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // Yıl ve hafta numarasından tarih hesaplama fonksiyonu
  const getDateFromWeek = (year, week) => {
    try {
      const startOfYearDate = startOfYear(new Date(year, 0, 1));
      const weekDate = setISOWeek(startOfYearDate, week);
      return format(weekDate, "d MMM yyyy", { locale: tr });
    } catch (error) {
      console.error("Tarih hesaplama hatası:", error);
      return `${week}. Hafta ${year}`;
    }
  };

  // Ay formatını düzenleme fonksiyonu
  const formatMonth = (monthStr) => {
    try {
      const [year, month] = monthStr.split("-");
      return format(new Date(year, month - 1), "MMMM yyyy", { locale: tr });
    } catch (error) {
      console.error("Ay formatı hatası:", error);
      return monthStr;
    }
  };
  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2, md: 3 },
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {/* Date Range Filter*/}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5, md: 3.5 },
          mb: { xs: 2, sm: 3, md: 4 },
          borderRadius: { xs: 2, sm: 3, md: 4 },
          background: "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
          border: "1px solid rgba(0,0,0,0.05)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: "absolute",
            top: -60,
            right: -60,
            width: 120,
            height: 120,
            borderRadius: "50%",
            background:
              "linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(25, 118, 210, 0.05) 100%)",
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            bottom: -20,
            left: -20,
            width: 80,
            height: 80,
            borderRadius: "50%",
            background:
              "linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.02) 100%)",
            zIndex: 0,
          }}
        />

        {/* Content */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: "text.primary",
              display: "flex",
              alignItems: "center",
              mb: 2.5,
            }}
          >
            <Box
              component="span"
              sx={{
                width: 16,
                height: 16,
                borderRadius: 1,
                mr: 1.5,
                background: "linear-gradient(135deg, #1976d2 0%, #2196f3 100%)",
              }}
            />
            Rapor Filtreleri
          </Typography>

          <Grid
            container
            spacing={{ xs: 2, sm: 3 }}
            sx={{ mb: { xs: 2, sm: 3 } }}
          >
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Başlangıç Tarihi"
                name="startDate"
                value={dateRange.startDate}
                onChange={handleDateChange}
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "white",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "primary.main",
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Bitiş Tarihi"
                name="endDate"
                value={dateRange.endDate}
                onChange={handleDateChange}
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "white",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "primary.main",
                    },
                  },
                }}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Button
              variant="contained"
              onClick={getReports}
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <UpdateIcon />
                )
              }
              sx={{
                px: 3,
                py: 1.2,
                borderRadius: 10,
                textTransform: "none",
                fontWeight: 600,
                background: "linear-gradient(90deg, #1976d2 0%, #2196f3 100%)",
                boxShadow: "0 4px 10px rgba(33, 150, 243, 0.3)",
                transition: "all 0.2s",
                "&:hover": {
                  boxShadow: "0 6px 15px rgba(33, 150, 243, 0.4)",
                },
              }}
            >
              {loading ? "Yükleniyor..." : "Raporları Güncelle"}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Statistics Cards */}
      <OrderStats 
        dateRangeTotalOrderCount={reports.dateRangeTotalOrderCount}
        dateRangeTotalRevenue={reports.dateRangeTotalRevenue}
        dateRange={dateRange}
      />

      {/* Sales Reports */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={12} md={6}>
          <ReportSection
            title="Haftalık Satış Raporu"
            data={
              reports.weeklySales?.map((row) => ({
                ...row,
                displayDate: getDateFromWeek(row.Year, row.Week),
                formattedSales: formatCurrency(row.TotalSales),
                totalSaleCount: row.TotalSaleCount,
              })) || []
            }
            columns={[
              {
                header: "Hafta",
                accessor: (row) => row.displayDate,
              },
              {
                header: "Toplam Satış Tutarı",
                accessor: (row) => row.formattedSales,
              },
              {
                header: "Toplam Satış Sayısı",
                accessor: (row) => row.totalSaleCount,
              },
            ]}
            loading={loading}
            chart={(data) => (
              <ResponsiveContainer width="100%" height={350} minHeight={300}>
                <LineChart
                  data={data}
                  margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f0f0f0"
                  />
                  <XAxis
                    dataKey="displayDate"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    tick={{ fontSize: 12, fill: "#555" }}
                    axisLine={{ stroke: "#E0E0E0" }}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#555" }}
                  />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={(label) => `Tarih: ${label}`}
                    contentStyle={{
                      backgroundColor: "white",
                      borderRadius: "10px",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                      border: "none",
                      padding: "10px 15px",
                    }}
                  />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{
                      paddingTop: "20px",
                      marginBottom: "-10px",
                    }}
                    iconType="circle"
                  />
                  <Line
                    type="monotone"
                    dataKey="TotalSales"
                    name="Satış Tutarı"
                    stroke="#6366F1"
                    strokeWidth={3}
                    dot={{ r: 4, strokeWidth: 2, fill: "white" }}
                    activeDot={{ r: 7, strokeWidth: 0, fill: "#6366F1" }}
                    animationDuration={1500}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ReportSection
            title="Aylık Satış Raporu"
            data={
              reports.monthlySales?.map((row) => ({
                ...row,
                displayMonth: formatMonth(`${row.Month}`),
                formattedSales: formatCurrency(row.TotalSales),
                totalSaleCount: row.TotalSaleCount,
              })) || []
            }
            columns={[
              {
                header: "Ay",
                accessor: (row) => row.displayMonth,
              },
              {
                header: "Toplam Satış Tutarı",
                accessor: (row) => row.formattedSales,
              },
              {
                header: "Toplam Satış Sayısı",
                accessor: (row) => row.totalSaleCount,
              },
            ]}
            loading={loading}
            chart={(data) => (
              <ResponsiveContainer width="100%" height={350} minHeight={300}>
                <AreaChart
                  data={data}
                  margin={{ top: 10, right: 30, left: 20, bottom: 30 }}
                >
                  <defs>
                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                      <stop
                        offset="95%"
                        stopColor="#82ca9d"
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="displayMonth"
                    label={{
                      value: "Ay",
                      position: "bottom",
                      fill: "#666",
                      fontSize: 14,
                    }}
                    tick={{ fill: "#666", fontSize: 12 }}
                    axisLine={{ stroke: "#E0E0E0" }}
                  />
                  <YAxis
                    label={{
                      angle: -90,
                      position: "insideLeft",
                      fill: "#666",
                      fontSize: 14,
                    }}
                    tick={{ fill: "#666", fontSize: 12 }}
                    axisLine={{ stroke: "#E0E0E0" }}
                  />
                  <Tooltip
                    formatter={(value, name) => [
                      formatCurrency(value),
                      "Satış Tutarı",
                    ]}
                    contentStyle={{
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      borderRadius: 8,
                      boxShadow: "0 2px 10px rgba(127, 159, 39, 0.1)",
                      border: "none",
                    }}
                  />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{
                      paddingTop: "15px",
                      marginBottom: "-5px",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="TotalSales"
                    name="Satış Tutarı"
                    stroke="#82ca9d"
                    fillOpacity={1}
                    fill="url(#colorSales)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          />
        </Grid>
      </Grid>

      {/* Top Products Reports */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              borderRadius: { xs: 2, sm: 3 },
              height: "100%",
              display: "flex",
              flexDirection: "column",
              background: "linear-gradient(to bottom right, #ffffff, #f8f9fa)",
              border: "1px solid #e0e0e0",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 2,
                pb: 2,
                borderBottom: "1px solid #e0e0e0",
                flexShrink: 0,
              }}
            >
              <LocalMallIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, fontSize: { xs: "1rem", sm: "1.1rem" } }}
              >
                Haftalık En Çok Satan Ürünler
              </Typography>
            </Box>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 400,
                }}
              >
                <CircularProgress size={40} />
              </Box>
            ) : reports.weeklyTopProducts?.length ? (
              <Box
                sx={{
                  maxHeight: 400,
                  overflow: "auto",
                  pr: 1,
                  mr: -1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#888",
                    borderRadius: "4px",
                    "&:hover": {
                      background: "#666",
                    },
                  },
                }}
              >
                {reports.weeklyTopProducts.map((product, index) => (
                  <TopProductCard
                    key={index}
                    date={getDateFromWeek(product.OrderYear, product.OrderWeek)}
                    productName={product.ProductName}
                    quantity={product.TotalQuantity}
                  />
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 120,
                  color: "text.secondary",
                }}
              >
                <InboxIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography variant="body1">Veri bulunamadı</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              borderRadius: { xs: 2, sm: 3 },
              height: "100%",
              display: "flex",
              flexDirection: "column",
              background: "linear-gradient(to bottom, #ffffff, #f5f5f5)",
              border: "1px solid #e0e0e0",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 2,
                pb: 2,
                borderBottom: "1px solid #e0e0e0",
                flexShrink: 0,
              }}
            >
              <LocalMallIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, fontSize: { xs: "1rem", sm: "1.1rem" } }}
              >
                Aylık En Çok Satan Ürünler
              </Typography>
            </Box>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 400,
                }}
              >
                <CircularProgress size={40} />
              </Box>
            ) : reports.monthlyTopProducts?.length ? (
              <Box
                sx={{
                  maxHeight: 400,
                  overflow: "auto",
                  pr: 1,
                  mr: -1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#888",
                    borderRadius: "4px",
                    "&:hover": {
                      background: "#666",
                    },
                  },
                }}
              >
                {reports.monthlyTopProducts.map((product, index) => (
                  <TopProductCard
                    key={index}
                    date={formatMonth(
                      `${product.OrderYear}-${product.OrderMonth}`
                    )}
                    productName={product.ProductName}
                    quantity={product.TotalQuantity}
                  />
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 120,
                  color: "text.secondary",
                }}
              >
                <InboxIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography variant="body1">Veri bulunamadı</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* VAT Reports */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={12} md={6}>
          <ReportSection
            title="Haftalık KDV Raporu"
            data={
              reports.weeklyVat?.map((row) => ({
                ...row,
                displayDate: getDateFromWeek(row.OrderYear, row.OrderWeek),
                formattedVat: formatCurrency(row.TotalVatAmount),
              })) || []
            }
            columns={[
              {
                header: "Hafta",
                accessor: (row) => row.displayDate,
              },
              {
                header: "Toplam KDV",
                accessor: (row) => row.formattedVat,
              },
            ]}
            loading={loading}
            chart={(data) => (
              <ResponsiveContainer width="100%" height={350} minHeight={300}>
                <LineChart
                  data={data}
                  margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f0f0f0"
                  />
                  <XAxis
                    dataKey="displayDate"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    tick={{ fontSize: 12, fill: "#555" }}
                    axisLine={{ stroke: "#E0E0E0" }}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#555" }}
                  />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={(label) => `Tarih: ${label}`}
                    contentStyle={{
                      backgroundColor: "white",
                      borderRadius: "10px",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                      border: "none",
                      padding: "10px 15px",
                    }}
                  />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{
                      paddingTop: "20px",
                      marginBottom: "-10px",
                    }}
                    iconType="circle"
                  />
                  <Line
                    type="monotone"
                    dataKey="TotalVatAmount"
                    name="KDV Tutarı"
                    stroke="#ff7043"
                    strokeWidth={3}
                    dot={{ r: 4, strokeWidth: 2, fill: "white" }}
                    activeDot={{ r: 7, strokeWidth: 0, fill: "#ff7043" }}
                    animationDuration={1500}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ReportSection
            title="Aylık KDV Raporu"
            data={
              reports.monthlyVat?.map((row) => ({
                ...row,
                displayMonth: formatMonth(`${row.OrderYear}-${row.OrderMonth}`),
                formattedVat: formatCurrency(row.TotalVatAmount),
              })) || []
            }
            columns={[
              {
                header: "Ay",
                accessor: (row) => row.displayMonth,
              },
              {
                header: "Toplam KDV",
                accessor: (row) => row.formattedVat,
              },
            ]}
            loading={loading}
            chart={(data) => (
              <ResponsiveContainer width="100%" height={350} minHeight={300}>
                <AreaChart
                  data={data}
                  margin={{ top: 10, right: 30, left: 20, bottom: 80 }}
                >
                  <defs>
                    <linearGradient id="colorVat" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                      <stop
                        offset="95%"
                        stopColor="#82ca9d"
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="displayMonth"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    tick={{ fontSize: 12, fill: "#666" }}
                    axisLine={{ stroke: "#E0E0E0" }}
                  />
                  <YAxis
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                    tick={{ fontSize: 12, fill: "#666" }}
                    axisLine={{ stroke: "#E0E0E0" }}
                  />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={(label) => `Ay: ${label}`}
                    contentStyle={{
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      borderRadius: 8,
                      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
                      border: "none",
                    }}
                  />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{
                      paddingTop: "15px",
                      marginBottom: "-5px",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="TotalVatAmount"
                    name="KDV Tutarı"
                    stroke="#82ca9d"
                    fillOpacity={1}
                    fill="url(#colorVat)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          />
        </Grid>
      </Grid>

      {/* Discount Reports */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={12} md={6}>
          <ReportSection
            title="Haftalık İndirim Raporu"
            data={
              reports.weeklyDiscounts?.map((row) => ({
                ...row,
                displayDate: getDateFromWeek(row.OrderYear, row.OrderWeek),
                formattedDiscount: formatCurrency(row.TotalWeeklyDiscount),
              })) || []
            }
            columns={[
              {
                header: "Hafta",
                accessor: (row) => row.displayDate,
              },
              {
                header: "Toplam İndirim",
                accessor: (row) => row.formattedDiscount,
              },
            ]}
            loading={loading}
            chart={(data) => (
              <ResponsiveContainer width="100%" height={350} minHeight={300}>
                <LineChart
                  data={data}
                  margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f0f0f0"
                  />
                  <XAxis
                    dataKey="displayDate"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    tick={{ fontSize: 12, fill: "#555" }}
                    axisLine={{ stroke: "#E0E0E0" }}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#555" }}
                  />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={(label) => `Tarih: ${label}`}
                    contentStyle={{
                      backgroundColor: "white",
                      borderRadius: "10px",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                      border: "none",
                      padding: "10px 15px",
                    }}
                  />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{
                      paddingTop: "20px",
                      marginBottom: "-10px",
                    }}
                    iconType="circle"
                  />
                  <Line
                    type="monotone"
                    dataKey="TotalWeeklyDiscount"
                    name="İndirim Tutarı"
                    stroke="#ff7043"
                    strokeWidth={3}
                    dot={{ r: 4, strokeWidth: 2, fill: "white" }}
                    activeDot={{ r: 7, strokeWidth: 0, fill: "#ff7043" }}
                    animationDuration={1500}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ReportSection
            title="Aylık İndirim Raporu"
            data={
              reports.monthlyDiscounts?.map((row) => ({
                ...row,
                displayMonth: formatMonth(`${row.OrderYear}-${row.OrderMonth}`),
                formattedDiscount: formatCurrency(row.TotalMonthlyDiscount),
              })) || []
            }
            columns={[
              {
                header: "Ay",
                accessor: (row) => row.displayMonth,
              },
              {
                header: "Toplam İndirim",
                accessor: (row) => row.formattedDiscount,
              },
            ]}
            loading={loading}
            chart={(data) => (
              <ResponsiveContainer width="100%" height={350} minHeight={300}>
                <AreaChart
                  data={data}
                  margin={{ top: 10, right: 30, left: 20, bottom: 80 }}
                >
                  <defs>
                    <linearGradient id="colorVat" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                      <stop
                        offset="95%"
                        stopColor="#82ca9d"
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="displayMonth"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    tick={{ fontSize: 12, fill: "#666" }}
                    axisLine={{ stroke: "#E0E0E0" }}
                  />
                  <YAxis
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                    tick={{ fontSize: 12, fill: "#666" }}
                    axisLine={{ stroke: "#E0E0E0" }}
                  />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={(label) => `Ay: ${label}`}
                    contentStyle={{
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      borderRadius: 8,
                      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
                      border: "none",
                    }}
                  />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{
                      paddingTop: "15px",
                      marginBottom: "-5px",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="TotalMonthlyDiscount"
                    name="Aylık İndirim Tutarı"
                    stroke="#82ca9d"
                    fillOpacity={1}
                    fill="url(#colorVat)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          />
        </Grid>
      </Grid>

      {/* Return Reports */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={12} md={6}>
          <ReportSection
            title="Haftalık İade Raporu"
            data={
              reports.weeklyReturns?.map((row) => ({
                ...row,
                displayDate: getDateFromWeek(row.OrderYear, row.OrderWeek),
                formattedReturn: formatCurrency(row.TotalReturnedAmount),
                returnedCount: row.TotalReturnedCount,
              })) || []
            }
            columns={[
              {
                header: "Hafta",
                accessor: (row) => row.displayDate,
              },
              {
                header: "Toplam İade Tutarı",
                accessor: (row) => row.formattedReturn,
              },
              {
                header: "Toplam İade Sayısı",
                accessor: (row) => row.returnedCount,
              },
            ]}
            loading={loading}
            chart={(data) => (
              <ResponsiveContainer width="100%" height={350} minHeight={300}>
                <LineChart
                  data={data}
                  margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f0f0f0"
                  />
                  <XAxis
                    dataKey="displayDate"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    tick={{ fontSize: 12, fill: "#555" }}
                    axisLine={{ stroke: "#E0E0E0" }}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#555" }}
                  />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={(label) => `Tarih: ${label}`}
                    contentStyle={{
                      backgroundColor: "white",
                      borderRadius: "10px",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                      border: "none",
                      padding: "10px 15px",
                    }}
                  />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{
                      paddingTop: "20px",
                      marginBottom: "-10px",
                    }}
                    iconType="circle"
                  />
                  <Line
                    type="monotone"
                    dataKey="TotalReturnedAmount"
                    name="İade Tutarı"
                    stroke="#ff7043"
                    strokeWidth={3}
                    dot={{ r: 4, strokeWidth: 2, fill: "white" }}
                    activeDot={{ r: 7, strokeWidth: 0, fill: "#ff7043" }}
                    animationDuration={1500}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ReportSection
            title="Aylık İade Raporu"
            data={
              reports.monthlyReturns?.map((row) => ({
                ...row,
                displayMonth: formatMonth(`${row.OrderYear}-${row.OrderMonth}`),
                formattedReturn: formatCurrency(row.TotalReturnedAmount),
                returnedCount: row.TotalReturnedCount,
              })) || []
            }
            columns={[
              {
                header: "Ay",
                accessor: (row) => row.displayMonth,
              },
              {
                header: "Toplam İade Tutarı",
                accessor: (row) => row.formattedReturn,
              },
              {
                header: "Toplam İade Sayısı",
                accessor: (row) => row.returnedCount,
              },
            ]}
            loading={loading}
            chart={(data) => (
              <ResponsiveContainer width="100%" height={350} minHeight={300}>
                <AreaChart
                  data={data}
                  margin={{ top: 10, right: 30, left: 20, bottom: 80 }}
                >
                  <defs>
                    <linearGradient id="colorVat" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                      <stop
                        offset="95%"
                        stopColor="#82ca9d"
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="displayMonth"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    tick={{ fontSize: 12, fill: "#666" }}
                    axisLine={{ stroke: "#E0E0E0" }}
                  />
                  <YAxis
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                    tick={{ fontSize: 12, fill: "#666" }}
                    axisLine={{ stroke: "#E0E0E0" }}
                  />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={(label) => `Ay: ${label}`}
                    contentStyle={{
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      borderRadius: 8,
                      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
                      border: "none",
                    }}
                  />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{
                      paddingTop: "15px",
                      marginBottom: "-5px",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="TotalReturnedAmount"
                    name="İade Tutarı"
                    stroke="#82ca9d"
                    fillOpacity={1}
                    fill="url(#colorVat)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          />
        </Grid>
      </Grid>

      {/* Cancellation Reports */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={12} md={6}>
          <ReportSection
            title="Haftalık İptal Raporu"
            data={
              reports.weeklyCancellations?.map((row) => ({
                ...row,
                displayDate: getDateFromWeek(row.OrderYear, row.OrderWeek),
                formattedCancel: formatCurrency(row.TotalCancelledAmount),
                cancelledCount: row.TotalCancelledCount,
              })) || []
            }
            columns={[
              {
                header: "Hafta",
                accessor: (row) => row.displayDate,
              },
              {
                header: "Toplam İptal Tutarı",
                accessor: (row) => row.formattedCancel,
              },
              {
                header: "Toplam İptal Sayısı",
                accessor: (row) => row.cancelledCount,
              },
            ]}
            loading={loading}
            chart={(data) => (
              <ResponsiveContainer width="100%" height={350} minHeight={300}>
                <LineChart
                  data={data}
                  margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f0f0f0"
                  />
                  <XAxis
                    dataKey="displayDate"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    tick={{ fontSize: 12, fill: "#555" }}
                    axisLine={{ stroke: "#E0E0E0" }}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#555" }}
                  />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={(label) => `Tarih: ${label}`}
                    contentStyle={{
                      backgroundColor: "white",
                      borderRadius: "10px",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                      border: "none",
                      padding: "10px 15px",
                    }}
                  />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{
                      paddingTop: "20px",
                      marginBottom: "-10px",
                    }}
                    iconType="circle"
                  />
                  <Line
                    type="monotone"
                    dataKey="TotalCancelledAmount"
                    name="İptal Tutarı"
                    stroke="#ff7043"
                    strokeWidth={3}
                    dot={{ r: 4, strokeWidth: 2, fill: "white" }}
                    activeDot={{ r: 7, strokeWidth: 0, fill: "#ff7043" }}
                    animationDuration={1500}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ReportSection
            title="Aylık İptal Raporu"
            data={
              reports.monthlyCancellations?.map((row) => ({
                ...row,
                displayMonth: formatMonth(`${row.OrderYear}-${row.OrderMonth}`),
                formattedCancelled: formatCurrency(row.TotalCancelledAmount),
                cancelledCount: row.TotalCancelledCount,
              })) || []
            }
            columns={[
              {
                header: "Ay",
                accessor: (row) => row.displayMonth,
              },
              {
                header: "Toplam İptal Tutarı",
                accessor: (row) => row.formattedCancelled,
              },
              {
                header: "Toplam İptal Sayısı",
                accessor: (row) => row.cancelledCount,
              },
            ]}
            loading={loading}
            chart={(data) => (
              <ResponsiveContainer width="100%" height={350} minHeight={300}>
                <AreaChart
                  data={data}
                  margin={{ top: 10, right: 30, left: 20, bottom: 80 }}
                >
                  <defs>
                    <linearGradient id="colorVat" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                      <stop
                        offset="95%"
                        stopColor="#82ca9d"
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="displayMonth"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    tick={{ fontSize: 12, fill: "#666" }}
                    axisLine={{ stroke: "#E0E0E0" }}
                  />
                  <YAxis
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                    tick={{ fontSize: 12, fill: "#666" }}
                    axisLine={{ stroke: "#E0E0E0" }}
                  />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={(label) => `Ay: ${label}`}
                    contentStyle={{
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      borderRadius: 8,
                      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
                      border: "none",
                    }}
                  />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{
                      paddingTop: "15px",
                      marginBottom: "-5px",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="TotalCancelledAmount"
                    name="İptal Tutarı"
                    stroke="#82ca9d"
                    fillOpacity={1}
                    fill="url(#colorVat)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          />
        </Grid>
      </Grid>

      {/* Other Reports */}
      {/* ... diğer rapor bölümleri benzer şekilde grid yapısında düzenlenebilir ... */}
    </Box>
  );
};
